import {selectUserContext} from 'store/authSlice';
import {FETCH_FAILURE_MESSAGE_RE} from 'utils/consts';
import {getDatadogLogs, getDatadogRum} from 'utils/datadog';
import {isCypress, isLocalhost} from 'utils/environment';

/**
 * -----------------------------
 * Parser & helper utilities
 * -----------------------------
 */

/** Parse and normalize error objects from various sources */
export const parseError = (error) => {
  if (!error) return {status: 513, data: {detail: 'Unknown error occurred'}};

  // HTML error bodies (e.g., 500 HTML)
  if (
    typeof error.data === 'string' &&
    error.data.toLowerCase().includes('<!doctype html>')
  ) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(error?.data, 'text/html');
    const text =
      doc.body.textContent?.replace(/\s+/g, ' ').trim() || 'Server error';
    return {...error, data: {detail: text}, originalHtml: error.data};
  }

  // Already normalized
  if (error.data?.detail && typeof error.data.detail === 'string') return error;

  // nested detail forms
  if (error.data?.detail?.data?.detail)
    return {...error, data: {detail: error.data.detail.data.detail}};
  if (error.data?.error?.detail)
    return {...error, data: {detail: error.data.error.detail}};

  const extractMessage = (err) => {
    if (err.status === 'FETCH_ERROR' && err.error) return err.error;
    if (err.data?.detail) return err.data.detail;
    if (err.data?.message) return err.data.message;
    if (err.data?.error) return err.data.error;
    if (typeof err.data === 'string') return err.data;
    if (err.message) return err.message;
    if (err.error) return err.error;
    return 'An unexpected error occurred';
  };

  const message = extractMessage(error);
  let processed = {...error};
  if (error.status === 'FETCH_ERROR') {
    processed.status = 0;
    processed.originalStatus = 'FETCH_ERROR';
  }

  return {
    ...error,
    ...processed,
    data: {detail: message, originalData: error.data},
  };
};

/** RTK Query meta extraction */
export const extractRequestMeta = (meta) => {
  if (!meta) return {};
  return {
    request: {
      url: meta.request?.url,
      method: meta.request?.method,
      data: meta.request?.data,
    },
    response: {
      status: meta.response?.status,
      statusText: meta.response?.statusText,
      data: meta.response?.data,
    },
  };
};

/** Map status/shape to a broad category */
export const categorizeError = (error) => {
  const status = error.status || error.originalStatus;
  if (status === 401) return 'authentication';
  if (status === 403) return 'authorization';
  if (status === 404) return 'notFound';
  if (status >= 400 && status < 500) return 'clientError';
  if (status >= 500) return 'serverError';
  if (status === 199) return 'misconfigured';
  if (
    !status &&
    error.name === 'TypeError' &&
    FETCH_FAILURE_MESSAGE_RE.test(error.message)
  )
    return 'networkError';

  if (status === 'FETCH_ERROR' && FETCH_FAILURE_MESSAGE_RE.test(error.error))
    return 'networkError';

  if (
    !status &&
    (error.code === 'ERR_NETWORK' || error.message === 'Network Error')
  )
    return 'networkError';

  return 'unknown';
};

/** External logging filter knob */
export const shouldLog = (error, {skipClientErrors = true} = {}) => {
  if (isCypress()) return false;
  const status = error.status || error.originalStatus;
  if (status >= 400 && status < 500 && skipClientErrors) return false;
  return true;
};

/** Consistent fingerprint for dedupe */
export const generateErrorFingerprint = (error) => {
  const parts = [
    error.name || 'Error',
    error.status || 'unknown',
    error.url || 'unknown',
    error.method || 'unknown',
    (error.message || '').substring(0, 50),
  ].filter(Boolean);
  return parts.join('-').replace(/[^a-zA-Z0-9-]/g, '');
};

/**
 * -----------------------------
 * Error classes
 * -----------------------------
 */
export class TrackError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'TrackError';
    this.status = options.status;
    this.code = options.code;
    this.url = options.url;
    this.method = options.method;
    this.originalError = options.originalError;
    this.context = options.context || {};
    this.timestamp = new Date().toISOString();
    this.type = options.type || 'unknown';
    if (options.originalError?.stack) {
      // Keep the original stack frames for debugging, but rewrite the header
      // line — Datadog RUM parses the stack (not `.name`/`.message`) to label
      // errors, so leaving the original error's header (e.g. "TypeError:
      // Failed to fetch") would surface as that instead of this wrapped type.
      const [, ...frames] = options.originalError.stack.split('\n');
      this.stack = [`${this.constructor.name}: ${message}`, ...frames].join(
        '\n'
      );
    }
  }
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      code: this.code,
      url: this.url,
      method: this.method,
      context: this.context,
      timestamp: this.timestamp,
      stack: this.stack,
      type: this.type,
    };
  }
}

export class ApiError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'ApiError';
    this.requestData = options.requestData;
    this.responseData = options.responseData;
    this.statusText = options.statusText;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      requestData: this.requestData,
      responseData: this.responseData,
      statusText: this.statusText,
    };
  }
}

export class AuthError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'AuthError';
    this.authType = options.authType;
    this.userInfo = options.userInfo;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      authType: this.authType,
      userInfo: this.userInfo,
    };
  }
}

export class ValidationError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'ValidationError';
    this.field = options.field;
    this.value = options.value;
    this.rule = options.rule;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      field: this.field,
      value: this.value,
      rule: this.rule,
    };
  }
}

export class NetworkError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'NetworkError';
    this.timeout = options.timeout;
    this.retryCount = options.retryCount || 0;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      timeout: this.timeout,
      retryCount: this.retryCount,
    };
  }
}

export class NotFoundError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'NotFoundError';
    this.resourceType = options.resourceType;
    this.resourceId = options.resourceId;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      resourceType: this.resourceType,
      resourceId: this.resourceId,
    };
  }
}
export class ComponentError extends TrackError {
  constructor(message, options = {}) {
    super(message, options);
    this.name = 'ComponentError';
    this.componentName = options.componentName;
    this.props = options.props;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      componentName: this.componentName,
      props: this.props,
    };
  }
}

/**
 * Enhanced creator that first normalizes, then selects a type
 */
export const createTrackError = (error, options = {}) => {
  if (error instanceof TrackError) return error;

  const normalized = parseError(error);
  // Spreading an Error (in parseError) drops `name`/`message` — they're
  // inherited/non-enumerable — so pass the originals through for categorizing.
  const category = categorizeError({
    name: error?.name,
    message: error?.message,
    ...normalized,
  });

  let message =
    normalized.data?.detail ||
    normalized.message ||
    error?.message ||
    'Unknown error';

  if (typeof message !== 'string') message = JSON.stringify(message);
  message = (message || '').trim() || 'An unknown error occurred';

  switch (category) {
    case 'authentication':
      return new AuthError(message, {
        ...options,
        ...normalized,
        authType: 'authentication',
        originalError: error,
      });
    case 'authorization':
      return new AuthError(message, {
        ...options,
        ...normalized,
        authType: 'authorization',
        originalError: error,
      });
    case 'networkError':
      return new NetworkError(message, {
        ...options,
        ...normalized,
        originalError: error,
      });
    case 'clientError':
    case 'serverError':
      return new ApiError(message, {
        ...options,
        ...normalized,
        requestData: normalized.request,
        responseData: normalized.response || normalized.data,
        originalError: error,
      });
    case 'notFound':
      return new NotFoundError(message, {
        ...options,
        status: 404,
        url: normalized.url || options.url,
        method: normalized.method || options.method,
        resourceType: options.resourceType,
        resourceId: options.resourceId,
        originalError: error,
      });
    case 'misconfigured':
      return new TrackError(message, {
        ...options,
        ...normalized,
        originalError: error,
      });
    default:
      if (error instanceof Error)
        return new TrackError(error.message, {
          ...options,
          ...normalized,
          originalError: error,
        });
      return new TrackError(message, {...options, originalError: error});
  }
};

export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

export function getErrorSeverity(error) {
  if (error instanceof ValidationError) return ERROR_SEVERITY.LOW;
  if (error instanceof AuthError) return ERROR_SEVERITY.LOW;
  if (error instanceof NotFoundError) return ERROR_SEVERITY.MEDIUM;
  if (error instanceof NetworkError) return ERROR_SEVERITY.HIGH;
  if (error instanceof ComponentError) return ERROR_SEVERITY.HIGH;
  if (error instanceof ApiError) {
    const s = error.status;
    if (s >= 500) return ERROR_SEVERITY.HIGH;
    if (s >= 400) return ERROR_SEVERITY.MEDIUM;
  }
  const s = error.status;
  if (s >= 500) return ERROR_SEVERITY.HIGH;
  if (s >= 400) return ERROR_SEVERITY.MEDIUM;
  if (s === 0) return ERROR_SEVERITY.HIGH; // network
  return ERROR_SEVERITY.MEDIUM;
}

/**
 * -----------------------------
 * Logger with Datadog + dedupe
 * -----------------------------
 */
const _isAuthError = (error) => {
  const msg = error?.message || '';
  const type = error?.name || error?.type || '';
  const status = error?.status || error?.response?.status;
  return (
    type === 'AuthError' ||
    type === 'UnauthorizedError' ||
    type === 'ForbiddenError' ||
    status === 401 ||
    status === 403 ||
    msg.includes('Invalid Token') ||
    msg.includes('Authentication failed') ||
    msg.includes('Unauthorized') ||
    msg.includes('Token expired') ||
    msg.includes('Login required') ||
    msg.includes('Session expired') ||
    msg.includes('Access denied') ||
    error?.data?.error_type === 'AuthError' ||
    error?.error_type === 'AuthError'
  );
};

const MAX_DIAGNOSTIC_EVENTS = 50;

const summarizeDiagnosticValue = (value) => {
  if (value == null) return value;
  if (typeof value === 'string') return value.slice(0, 500);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.slice(0, 10).map((item) => summarizeDiagnosticValue(item));
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 20)
        .map(([key, entryValue]) => [key, summarizeDiagnosticValue(entryValue)])
    );
  }
  return String(value).slice(0, 500);
};

const sanitizeDiagnosticDetails = (details = {}) =>
  Object.fromEntries(
    Object.entries(details).map(([key, value]) => [
      key,
      summarizeDiagnosticValue(value),
    ])
  );

class ErrorLogger {
  constructor() {
    this.isLocalhost = isLocalhost();
    this.isCypress = isCypress();
    this.recentErrors = new Map();
    this.recentDiagnostics = [];
    this.dedupWindow = 5000;
    this.maxRecentErrors = 100;
    this.logCount = 0;
    this.startTime = Date.now();
    this.setupGlobalErrorHandlers();
  }

  setupGlobalErrorHandlers() {
    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      const error = event.error || new Error(event.message);
      this.logError(error, {
        context: {
          source: 'window.error',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          userAgent: navigator.userAgent,
          url: window.location.href,
          errorType: 'uncaught',
        },
        includeDiagnostics: true,
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      this.logError(error, {
        context: {
          source: 'unhandledrejection',
          reason: event.reason,
          userAgent: navigator.userAgent,
          url: window.location.href,
          errorType: 'unhandledRejection',
        },
        includeDiagnostics: true,
      });
    });

    console.debug('Global error handlers initialized');
  }

  recordDiagnosticEvent(stage, details = {}) {
    const entry = {
      stage,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      activeElement: document.activeElement?.tagName?.toLowerCase() || null,
      ...sanitizeDiagnosticDetails(details),
    };

    this.recentDiagnostics.push(entry);
    if (this.recentDiagnostics.length > MAX_DIAGNOSTIC_EVENTS) {
      this.recentDiagnostics = this.recentDiagnostics.slice(
        -MAX_DIAGNOSTIC_EVENTS
      );
    }

    return entry;
  }

  getRecentDiagnostics(limit = 10) {
    return this.recentDiagnostics.slice(-limit);
  }

  getContext(state) {
    // If state isn't provided (e.g. global window error),
    // it falls back to empty or a global ref if you really need it.
    const user = state ? selectUserContext(state) : null;
    // Logout/session-expiry clears redux auth state before some logs fire.
    // The RUM SDK's own user (set via setDatadogUser, never cleared on
    // logout) still has the last-known identity — fall back to it so the
    // log entry isn't attributed to no one.
    const rumUser = user?.sub ? null : getDatadogRum()?.getUser?.();

    return {
      location: window.location.href,
      user: {
        id: user?.sub ?? rumUser?.id,
        email: user?.email ?? rumUser?.email,
        name: user?.name ?? rumUser?.name,
        ps_site_codes: user?.ps_site_codes ?? rumUser?.ps_site_codes,
        permissions: user?.permissions ?? rumUser?.permissions,
      },
      session: {id: getDatadogRum()?.getInternalContext?.()?.session_id},
      performance: {
        logCount: this.logCount,
        uptimeMs: Date.now() - this.startTime,
      },
    };
  }

  shouldDeduplicate(error) {
    const fp = generateErrorFingerprint(error);
    const now = Date.now();
    const last = this.recentErrors.get(fp);
    if (last && now - last < this.dedupWindow) return true;
    this.recentErrors.set(fp, now);
    if (this.recentErrors.size > this.maxRecentErrors) this.cleanOldErrors();
    return false;
  }

  cleanOldErrors() {
    const now = Date.now();
    for (const [fp, ts] of this.recentErrors.entries()) {
      if (now - ts > this.dedupWindow) this.recentErrors.delete(fp);
    }
  }

  // 404s are expected in many flows (e.g. existence checks), and 401 AuthErrors
  // for unauthenticated users are expected (login flows, session expiry
  // polling) — both are excluded from Datadog to avoid noise.
  willReportToDatadog(error, context) {
    if (error instanceof NotFoundError) return false;
    if (error instanceof AuthError && error.status === 401 && !context.user?.id)
      return false;
    return true;
  }

  logToConsole(error, severity, context) {
    const isDatadogReported = this.willReportToDatadog(error, context);
    const payload = {
      error: error.toJSON?.() ?? error,
      context,
      severity,
      status: error.status ?? error.toJSON?.()?.status,
    };
    // Errors reported to Datadog via rum.addError use console.debug here to
    // avoid RUM double-counting console.error as a separate event.
    if (isDatadogReported) {
      console.debug(`[TRACK ${error.name}]`, payload);
      return;
    }
    switch (severity) {
      case ERROR_SEVERITY.CRITICAL:
      case ERROR_SEVERITY.HIGH:
        console.error('[TRACK Error]', payload);
        break;
      case ERROR_SEVERITY.MEDIUM:
        if (error.status === 401 || error.status === 403)
          console.warn('[TRACK Auth Warning]', payload);
        else console.error('[TRACK Error]', payload);
        break;
      case ERROR_SEVERITY.LOW:
        console.warn('[TRACK Warning]', payload);
        break;
      default:
        console.log('[TRACK Info]', payload);
    }
  }

  logToDatadog(error, severity, context) {
    if (!shouldLog(error, {skipClientErrors: false})) return;
    try {
      const fingerprint = generateErrorFingerprint(error);
      const base = {
        ...(error.toJSON?.() ?? {}),
        severity,
        context,
        fingerprint,
        status: error.status ?? error.toJSON?.()?.status,
        statusCode: error.status ?? error.toJSON?.()?.status,
      };
      const logs = getDatadogLogs();
      if (logs) {
        const level = this.getDatadogLogLevel(severity);
        const statusText = error.status ? ` [${error.status}]` : '';
        const errorType = error.name || 'TrackError';
        const urlPath = error.url ? ` ${new URL(error.url).pathname}` : '';
        logs.logger[level](`${errorType}${statusText}${urlPath}`, base);
      }
      const rum = getDatadogRum();
      if (rum) {
        rum.addError(error, {
          ...base,
          errorBoundary: context.errorType === 'component',
          userAction: context.userAction || null,
        });
      }
    } catch (e) {
      console.error('Failed to log error to Datadog:', e);
    }
  }

  getDatadogLogLevel(severity) {
    if (severity === ERROR_SEVERITY.CRITICAL) return 'error';
    if (severity === ERROR_SEVERITY.HIGH) return 'error';
    if (severity === ERROR_SEVERITY.MEDIUM) return 'warn';
    return 'info';
  }

  logError(error, options = {}, state = null) {
    const trackError = createTrackError(error, options.context);

    if (this.shouldDeduplicate(trackError)) return trackError;

    const severity = options.severity || getErrorSeverity(trackError);
    // Pass state to getContext
    const context = {
      ...this.getContext(state),
      ...options.context,
      diagnostics:
        options.includeDiagnostics === false
          ? undefined
          : this.getRecentDiagnostics(),
    };

    this.logCount++;
    this.logToConsole(trackError, severity, context);
    if (!this.willReportToDatadog(trackError, context)) return trackError;
    this.logToDatadog(trackError, severity, context);
    return trackError;
  }

  logApiError(error, requestMeta = {}, state = null) {
    return this.logError(
      error,
      {context: {requestMeta, errorType: 'api'}},
      state
    );
  }

  logComponentError(error, componentName, props = {}) {
    return this.logError(error, {
      context: {
        componentName,
        props: this.sanitizeProps(props),
        errorType: 'component',
      },
    });
  }

  logAuthError(error, authContext = {}) {
    return this.logError(error, {
      context: {authContext, errorType: 'authentication'},
    });
  }

  logValidationError(error, formData = {}) {
    return this.logError(error, {
      severity: ERROR_SEVERITY.LOW,
      context: {
        formData: this.sanitizeFormData(formData),
        errorType: 'validation',
      },
    });
  }

  sanitizeProps(props) {
    const s = {...props};
    Object.keys(s).forEach((k) => {
      if (typeof s[k] === 'function') s[k] = '[Function]';
      const low = k.toLowerCase();
      if (
        low.includes('password') ||
        low.includes('token') ||
        low.includes('secret')
      )
        s[k] = '[REDACTED]';
    });
    return s;
  }

  sanitizeFormData(formData) {
    const s = {...formData};
    Object.keys(s).forEach((k) => {
      const low = k.toLowerCase();
      if (
        low.includes('password') ||
        low.includes('token') ||
        low.includes('secret')
      )
        s[k] = '[REDACTED]';
    });
    return s;
  }

  getStats() {
    return {
      totalLogged: this.logCount,
      recentErrorsCount: this.recentErrors.size,
      recentDiagnosticsCount: this.recentDiagnostics.length,
      uptimeMs: Date.now() - this.startTime,
      environment: {isLocalhost: this.isLocalhost, isCypress: this.isCypress},
    };
  }
  reset() {
    this.recentErrors.clear();
    this.recentDiagnostics = [];
    this.logCount = 0;
    this.startTime = Date.now();
  }
  isAuthError(error) {
    return _isAuthError(error);
  }
}

// Singleton
const errorLogger = new ErrorLogger();
export default errorLogger;
export {ErrorLogger, _isAuthError as isAuthError};
export const recordDiagnosticEvent = (stage, details = {}) =>
  errorLogger.recordDiagnosticEvent(stage, details);
export const getRecentDiagnostics = (limit = 10) =>
  errorLogger.getRecentDiagnostics(limit);

/**
 * Convenience function to manually log errors from anywhere in the app
 * This provides a simple interface that replaces the removed datadog.js reportError
 *
 * Usage:
 * import { reportError } from 'utils/errors';
 *
 * try {
 *   // some code that might fail
 * } catch (error) {
 *   reportError(error, { component: 'MyComponent', action: 'fetchData' });
 * }
 */
export const reportError = (error, context = {}) => {
  return errorLogger.logError(error, {context});
};
